import Merengue from '@/infra/api/Merengue';
import PoiSearchLog from '@/infra/log/PoiSearchLog';
import Spot from '@/model/spot/Spot';
import store from '@/store';
import 'polyfill-array-includes';

/**
 * フリーワード検索時に住所検索を合わせて実施する確度の境界値を 0 ~ 1 の範囲で指定
 * @type {number}
 */
const BORDER_OF_USING_ADDRESSES = 0.5;
/**
 * スポット配列の actions
 */
const actions = {
  /**
   * 検索条件とその検索結果をリセットする。
   */
  resetSearchParams: ({commit}) => {
    // 検索条件
    commit('setSearchParams', {
      lang: 'ja',
      word: '',
      coord: '',
      area: '',
      category: '',
      tag: '',
      areaName: '',
      providerCode: '',
      feature: '',  // 特集スポット検索
      spotName: '', // 周辺検索
      requirement: '',  // 特集スポット検索
      status: '',  // 特集スポット検索
      tags: '', // スポット検索絞り込み条件
    });
    // 検索結果
    commit('setSearchResultSpots', []);
    commit('setSearchResultAddresses', []);
    commit('setErrorMessageIdSpot', '');
    commit('setErrorMessageIdAddress', '');
  },

  resetSearchWords: ({commit, state}) => {
    state.searchParams.word = '';
  },
  /**
   * 指定した地点リスト検索条件から、その検索条件とその検索結果を保存する。
   */
  setSearchParams: async ({commit, state}, payload) => {
    const lang = payload.lang;
    const siteId = payload.siteId;
    const requestParams = {
      version: payload.version,
      word: payload.word,
      coord: payload.coord,
      area: payload.area,
      address: payload.address,
      type: payload.type,
      category: payload.category,
      tag: payload.tag,
      areaName: payload.areaName,
      providerCode: payload.providerCode,
      date: payload.date,
      limit: 40,  // 検索数固定
      popularSpot: payload.popularSpot || false,
      add: payload.add,
      without_children: payload.withoutChildren,
      reduce_redundant_relations: payload.reduceRedundantRelations,
      radius: payload.radius || 2000,
      shape_coord: payload?.shapeCoord,
      scoring_mode: payload?.scoringMode,
      box: payload?.box,
    };

    // 検索条件を保存
    commit('setSearchParams', payload);

    const merengue = new Merengue(siteId);
    let usesAddress = false;
    // 住所を指定していると思われるフリーワードの正規表現パターン
    const addressMatcher = new RegExp("^[^0-9０-９].*[0-9０-９]{1,5}([-|－|−|ー]).*[0-9０-９]{1,5}");
    if (requestParams.word && addressMatcher.test(requestParams.word)) {
      // フリーワードが正規表現のパターンに一致すれば住所検索も実行する
      usesAddress = true;
    } else if (requestParams.word &&
      !requestParams.area && !requestParams.coord && !requestParams.tag && !requestParams.category) {
      // サイト ID（≒公式サービスかどうか）で POI 種別推定を利用できるかを判定
      const usesEstimate = siteId === 'navitime-travel';
      if (usesEstimate) {
        // フリーワードのみを指定した場合に住所検索も実行するかを確認
        merengue.getPoiTypeEstimate(siteId, lang, {word: requestParams.word}).then(res => {
          if (res.data.items.some(item => item.name === 'address')) {
            const address = res.data.items.find(item => item.name === 'address');
            usesAddress = address.confidence >= BORDER_OF_USING_ADDRESSES;
          }
        });
      } else {
        usesAddress = true;
      }
    }
    if (payload.box) {
      // box の指定がある場合は /spot/list/v2 を利用
      commit('isSearchingSpot', true);
      const params = merengue.convertSearchParamInfoRequestParam(requestParams);
      await merengue.getSpot(siteId, lang, params, !payload.isMobile)
        .then((res) => {
          commit('setErrorMessageIdSpot', '');
          let showSpots = res.data.items ?? res.data.spotList?.map(spot => new Spot(spot));
          if (!showSpots || !showSpots.length) {
            // 結果なし
            commit('setErrorMessageIdSpot', 'common_search_none');
            showSpots = [];
          }
          commit('setSearchResultSpots', showSpots);
        }).catch(e => {
          commit('setSearchResultSpots', []);
          commit('setErrorMessageIdSpot', 'common_connection_error');
          console.error(e, payload);
        }).finally(() => {
          commit('isSearchingSpot', false);
        });
    }
    if (requestParams.tag || requestParams.word || requestParams.address || requestParams.area || requestParams.coord ||
      requestParams.category || requestParams.shape_coord) {
      // 検索条件がある場合
      commit('isSearchingSpot', true);
      // 検索して結果を保存（スポット）
      const merengue = new Merengue(siteId);
      const params = merengue.convertSearchParamInfoRequestParam(requestParams);
      await merengue.getSpot(siteId, lang, params, !payload.isMobile)
        .then((res) => {
          commit('isSearchingSpot', false);
          commit('setErrorMessageIdSpot', '');

          let showSpots = res.data.items ?? res.data.spotList?.map(spot => new Spot(spot));
          if (!showSpots || showSpots.length === 0) {
            // 結果なし
            commit('setErrorMessageIdSpot', 'common_search_none');
            showSpots = [];
          }
          commit('setSearchResultSpots', showSpots);

          // POI検索改善ログ処理
          if (payload.sendLog && state.poiSearchLog) {
            state.poiSearchLog.sendSearchSpotTiming(requestParams.word, showSpots, '', payload.moveType);
          }

          if (usesAddress) {
            // 検索して結果を保存（住所）
            // 並列にしたいですが、POI検索ログの実装を楽にするために
            // 一旦スポット検索後に住所検索にしています（2018/12）
            commit('isSearchingAddress', true);
            requestParams.address = store.state.system.customize.targetArea;
            merengue.getAddressList(siteId, lang, requestParams)
              .then((res) => {
                commit('isSearchingAddress', false);
                commit('setErrorMessageIdAddress', '');

                if (!res.data.items || res.data.items.length === 0) {
                  // 結果なし
                  commit('setErrorMessageIdAddress', 'common_search_none');
                  commit('setSearchResultAddresses', []);
                } else {
                  // 結果あり
                  commit('setSearchResultAddresses', res.data.items);
                }
                // POI検索改善ログ処理
                if (payload.sendLog && state.poiSearchLog) {
                  state.poiSearchLog.sendSearchAddressTiming(requestParams.word, res.data.items, '');
                }
              })
              .catch((e) => {
                commit('isSearchingAddress', false);
                commit('setSearchResultAddresses', []);
                commit('setErrorMessageIdAddress', 'common_connection_error');
                console.error(e, payload);
              });
          } else {
            commit('setSearchResultAddresses', []);
          }
        })
        .catch((e) => {
          commit('isSearchingSpot', false);
          commit('setSearchResultSpots', []);
          commit('setErrorMessageIdSpot', 'common_connection_error');
          console.error(e, payload);
        });
    } else {
      // 検索条件がない場合
      commit('setSearchResultSpots', []);
      commit('setSearchResultAddresses', []);
    }
  },

  /**
   * 地点詳細情報を表示
   */
  showSpotDetail: ({commit}, payload) => {
    payload.visible = true;
    // スポット詳細情報を設定
    commit('spotDetail', payload);
  },

  /**
   * 地点詳細情報を非表示
   */
  dismissSpotDetail: ({commit}, payload) => {
    commit('spotDetail', {spot: null, visible: false});
  },

  /**
   * 地図に表示する地点リストを設定
   */
  setResultSpots: ({commit}, payload) => {
    commit('setResultSpots', payload);
  },

  /**
   * 地点検索結果リストでホバー中または地点詳細表示中の地点を設定
   */
  setFocusedPoi({commit}, payload) {
    commit('setFocusedPoi', payload);
  },

  /**
   * MapPageにて、一時的に選択されたスポットをセットする
   */
  setTapSpot({commit}, payload) {
    commit('setTapSpot', payload);
  },

  /**
   * MapPageにて、描画に使用するスポットたちを一括でリセットする
   */
  resetMapSpots({commit}) {
    commit('setTapSpot', null);
    commit('setFocusedPoi', null);
    commit('setSearchResultSpots', []);
    store.dispatch('map/deleteInfoWindow', {}, {root: true});
  },

  /**
   * フリーワード検索
   * 指定した地点リスト検索条件から、その検索条件とその検索結果を保存する。
   */
  searchSpotListByFreeWord: ({commit, state}, payload) => {
    store.dispatch('map/setSelectedSpotTab', 'spotSearch', {root: true});
    if (!payload.word || payload.word === '') return;
    commit('isSearchingSpot', true);
    commit('setRecommended', false);
    commit('setTapSpot', null);

    let searchParam = {
      lang: payload.lang,
      word: payload.word || undefined,
      address: payload.address || undefined,
      category: payload.category || undefined,
      limit: payload.limit || 100,  // 検索数
      add: payload?.add,
      // filterCurrentLocationを見てスポット検索の中心点を固定するか設定
      type: payload.type,
      nassCondition: payload.nassCondition,
      has_article: state.filterArticleSpot,
    };

    // FW検索では記事フィルタを適用させない
    commit('setFilterArticleSpot', false);

    const radius = actions.fitRadius({commit, state});
    if (state.filterCurrentLocation && radius &&
      state.currentLocation && state.currentLocation.lat &&
      state.currentLocation.lon) {
      // 現在地周辺で検索フィルタを設定している場合、緯度経度にスポット検索の中心点を設定
      searchParam['coord'] =
        state.currentLocation.lat + ',' + state.currentLocation.lon;
      searchParam['radius'] = radius || 10000;
    } else if (state.filterShowArea) {
      // 地図の表示範囲を取得して shape_coord を設定
      searchParam['shape_coord'] = payload.shapeCoord;
    }

    commit('setSearchParams', searchParam);
    const merengue = new Merengue(payload.siteId);
    const params = merengue.convertSearchParamInfoRequestParam(searchParam);
    const usesMocha = params.type === 'mocha';
    if (usesMocha) {
      // 2024/06/10 時点で mocha 形式のレスポンスを受け取るのは PC ページであることから PC ページで利用しているスポット検索のフローを利用
      actions.setSearchParams({commit}, payload);
    } else {
      commit('setSearchParams', searchParam);
      merengue.getSpot(payload.siteId, payload.lang, params, false).then((res) => {
        let spotList = usesMocha
          ? res.data.items
          : res.data.spotList.length > 0 ? res.data.spotList : [];
        commit('setErrorMessageIdSpot', '');
        actions.filterSpotList({commit, state}, {spotList: spotList, usesMocha});
      }).catch((e) => {
        commit('setErrorMessageIdSpot', 'common_connection_error');
        console.log(e, payload);
      }).finally(() => {
        commit('isSearchingSpot', false);
      });
    }
  },

  /**
   * 指定したカテゴリコードや位置情報からスポット情報を検索する。
   */
  searchSpotListByCategory: ({commit, state}, payload) => {
    store.dispatch('map/setSelectedSpotTab', 'spotSearch', {root: true});
    if (!payload.category || payload.category === '' || !payload.category.type) return;
    commit('isSearchingSpot', true);
    commit('setRecommended', false);
    commit('setTapSpot', null);

    let searchParam = {
      lang: payload.lang,
      word: payload.word || undefined,
      address: payload.address || undefined,
      category: payload.category || undefined,
      type: payload.category.type,
      limit: payload.limit || 100,  // 検索数
      has_article: state.filterArticleSpot,
    };

    const radius = actions.fitRadius({commit, state});
    if (state.filterCurrentLocation && radius &&
      state.currentLocation && state.currentLocation.lat &&
      state.currentLocation.lon) {
      // 現在地周辺で検索フィルタを設定している場合、緯度経度にスポット検索の中心点を設定
      searchParam['coord'] =
        state.currentLocation.lat + ',' + state.currentLocation.lon;
      searchParam['radius'] = radius || 10000;
    } else if (state.filterShowArea) {
      // 地図の表示範囲を取得して shape_coord を設定
      searchParam['shape_coord'] = payload.shapeCoord;
    }

    commit('setSearchParams', searchParam);
    const merengue = new Merengue(payload.siteId);
    const params = merengue.convertSearchParamInfoRequestParam(searchParam);
    merengue.getSpot(payload.siteId, payload.lang, params, false).then((res) => {
      let spotList = res.data.spotList.length > 0 ? res.data.spotList : [];
      commit('isSearchingSpot', false);
      commit('setErrorMessageIdSpot', '');
      actions.filterSpotList({commit, state}, {spotList: spotList});
    }).catch((e) => {
      commit('isSearchingSpot', false);
      commit('setErrorMessageIdSpot', 'common_connection_error');
      console.log(e, payload);
    });
  },

  /**
   * 指定したスポット群の情報を取得する。
   * 緯度経度指定等の周辺検索機能は無い
   */
  fetchSpotSetList: ({commit, state}, payload) => {
    store.dispatch('map/setSelectedSpotTab', 'spotSearch', {root: true});
    if (!payload.spotSet || payload.spotSet === '') return;
    commit('isSearchingSpot', true);
    commit('setRecommended', false);
    commit('setTapSpot', null);
    // 「絞り込み」機能は保証できていないため非表示にする
    store.dispatch('system/setShowResultTools', false, {root: true});

    let searchParam = {
      lang: payload.lang,
      category: payload.spotSet,
      tag: payload.tag,
      limit: 100,  // 検索数
    };

    commit('setSearchParams', searchParam);
    const merengue = new Merengue(payload.siteId);
    const params = merengue.convertSearchParamInfoRequestParam(searchParam);
    merengue.getSpot(payload.siteId, payload.lang, params, false).then((res) => {
      let spotList = res.data.spotList.length > 0 ? res.data.spotList : [];
      commit('isSearchingSpot', false);
      commit('setErrorMessageIdSpot', '');
      actions.filterSpotList({commit, state}, {spotList: spotList});
    }).catch((e) => {
      commit('isSearchingSpot', false);
      commit('setErrorMessageIdSpot', 'common_connection_error');
      console.log(e, payload);
    });
  },

  /**
   * おすすめスポット検索を行う。
   */
  searchRecommendSpotList: ({commit, state}, payload) => {
    store.dispatch('map/setSelectedSpotTab', 'spotSearch', {root: true});
    commit('isSearchingSpot', true);
    commit('setTapSpot', null);
    const merengue = new Merengue(payload.siteId);
    return merengue.getSpot(payload.siteId, payload.lang, payload, false).then((res) => {
      let spotList = res.data.spotList.length > 0 ? res.data.spotList : [];
      commit('isSearchingSpot', false);
      commit('setRecommended', true);
      commit('setErrorMessageIdSpot', '');
      spotList = spotList.map(spot => new Spot(spot));
      actions.filterSpotList({commit, state}, {spotList: spotList});
    }).catch((e) => {
      commit('isSearchingSpot', false);
      commit('setRecommended', false);
      commit('setErrorMessageIdSpot', 'common_connection_error');
      console.error(e, payload);
    });
  },
  /**
   * ズーム値に応じて適切な検索の半径を取得する
   */
  fitRadius: ({commit, state}) => {
    const zoom = store.state.map.map.getZoom();
    if (zoom <= 8) return 50000;
    else if (zoom === 9) return 50000;
    else if (zoom === 10) return 30000;
    else if (zoom === 11) return 15000;
    else if (zoom === 12) return 7000;
    else if (zoom === 13) return 5000;
    else if (zoom === 14) return 2000;
    else if (zoom === 15) return 1000;
    else if (zoom === 16) return 500;
    else if (zoom === 17) return 200;
    else if (zoom === 18) return 100;
    else if (zoom >= 19) return 50;
    else return null;
  },

  /**
   * 指定したカテゴリコードからスポット情報を取得する。
   */
  filterSpotList: ({commit, state}, payload) => {
    if (!payload.spotList && payload.spotList.length === 0) return;
    let spotList = payload.spotList;
    if (spotList.length === 0) {
      commit('setErrorMessageIdSpot', 'common_search_none');
    } else {
      spotList = spotList.map(spot => new Spot(spot));

      // 結果に対してフィルタリングかける
      if (state.filterCreditCard) {
        spotList = spotList.filter(spot => spot.canUseCreditCard);
      }
      if (state.filterParking) {
        spotList = spotList.filter(spot => spot.parkingAvailable);
      }
      if (state.filterBarrierFree) {
        spotList = spotList.filter(spot => spot.wheelchairAccessible);
      }
      if (state.filterWiFi) {
        spotList = spotList.filter(spot => spot.canUseWifi);
      }
      if (state.filterPlug) {
        spotList = spotList.filter(spot => spot.canUsePlug);
      }
      if (state.filterMultilingual) {
        spotList = spotList.filter(spot => spot.supportMultilang);
      }
      if (state.filterVegan) {
        spotList = spotList.filter(spot => spot.supportVegan);
      }
      if (state.filterHighLate) {
        spotList = spotList.filter(spot => parseFloat(spot.tripAdvisor.rating) >= 0.0);
        spotList.sort((a, b) => {
          if (a.tripAdvisor.rating > b.tripAdvisor.rating) return -1;
          if (a.tripAdvisor.rating < b.tripAdvisor.rating) return 1;
          return 0;
        });
      }
      if (state.filterNumSeats !== 'unset') {
        if (state.filterNumSeats === 'small') {
          spotList = spotList.filter(spot => parseInt(spot.numSeats) > -1 && parseInt(spot.numSeats) <= 10);
        } else if (state.filterNumSeats === 'middle') {
          spotList = spotList.filter(spot => parseInt(spot.numSeats) > 10 && parseInt(spot.numSeats) <= 30);
        } else if (state.filterNumSeats === 'large') {
          spotList = spotList.filter(spot => parseInt(spot.numSeats) > 30);
        }
      }
    }
    commit('setSearchResultSpots', spotList);
  },

  /**
   * フィルタを適用して、
   * 指定した地点リスト検索条件から、その検索条件とその検索結果を保存する。
   */
  searchSpotListWithFilter: ({commit, state}, payload) => {
    state.searchParams.siteId = payload.siteId;
    state.searchParams.shapeCoord = payload.shapeCoord;
    if (state.searchParams.hasOwnProperty('word') && state.searchParams.word) {
      actions.searchSpotListByFreeWord({commit, state}, state.searchParams);
    } else if (state.searchParams.hasOwnProperty('category') && state.searchParams.category) {
      actions.searchSpotListByCategory({commit, state}, state.searchParams);
    }
  },

  /**
   * スポットのサジェストをセットする
   * 現在はmochaのオートコンプリートを使用
   */
  spotAutocomplete: ({commit}, payload) => {
    if (payload.word.length === 0) {
      commit('setSpotSuggestList', []);
      return;
    }
    if (payload.word.length < 2) return;
    if (payload.word.match(/^\s+$/)) return;

    new Merengue(payload.siteId).getAutocompleteSpotsWord(payload.siteId, payload.lang, payload)
      .then((res) => {
        let spotList = res.data.spotList;
        if (spotList && spotList.length > 0) {
          // 速度重視でSpotオブジェクトの変換をやめる
          // spotList = spotList.map(spot => new Spot(spot));
          commit('setSpotSuggestList', spotList);
        }
      })
      .catch((e) => {
        commit('setErrorMessageIdSpot', 'common_connection_error');
        console.log(e, payload);
      });
  },

  /**
   * サジェストから選ばれたスポット
   */
  setSelectedSpotNameInSuggest: ({commit, state}, payload) => {
    commit('setSelectedSpotNameInSuggest', payload);
  },

   /**
   * サジェストから選ばれたスポット
   */
  setSelectedCategory: ({commit, state}, payload) => {
    commit('setSelectedCategory', payload);
  },

  /**
   * 指定したスポットコードからスポット情報を取得する。
   */
  searchSpotListBySpotCodes: ({commit}, payload) => {
    store.dispatch('map/setSelectedSpotTab', 'spotSearch', {root: true});
    commit('isSearchingSpot', true);
    actions.resetSearchParams({commit});

    const merengue = new Merengue(payload.siteId);
    const params = merengue.convertSearchParamInfoRequestParam({id: payload.spotCode});
    // 端末の種別によって取得するレスポンスタイプを切り替える
    merengue.getSpot(payload.siteId, payload.lang, params, !payload.isMobile)
      .then((res) => {
        commit('isSearchingSpot', false);
        commit('setErrorMessageIdSpot', '');

        let spotList = payload.isMobile ? res.data.spotList : res.data.items;
        if (!spotList || !spotList.length) {
          // 結果なし
          commit('setErrorMessageIdSpot', 'common_search_none');
          spotList = [];
        } else {
          spotList = spotList.map(spot => payload.isMobile ? new Spot(spot) : spot);
        }
        commit('setSearchResultSpots', spotList);
        if (!payload.isMobile && spotList.length) {
          // PC ではスポット詳細コンポーネントを表示する
          actions.showSpotDetail({commit}, {spot: spotList[0], from: 'autocomplete'});
        }
      })
      .catch((e) => {
        commit('isSearchingSpot', false);
        commit('setErrorMessageIdSpot', 'common_connection_error');
        console.log(e, payload);
      });
  },

  /**
   * 指定した緯度経度からスポット情報を取得する。
   */
  searchSpotListByCoord: ({commit}, payload) => {
    commit('isSearchingSpot', true);
    actions.resetSearchParams({commit});

    new Merengue(payload.siteId).getSpotByCoord(payload.siteId, payload.lang, {
      pin_coord: payload.coord,
      pin_name: payload.name
    }).then((res) => {
      commit('isSearchingSpot', false);
      commit('setErrorMessageIdSpot', '');

      let spotList = [];
      const spot = res.data.spot;
      if (!spot) {
        // 結果なし
        commit('setErrorMessageIdSpot', 'common_search_none');
      } else {
        // 緯度経度に「pinSpot」というIDを持たせる
        spot['spotId'] = 'pinSpot';
        spotList = [new Spot(spot)];
      }
      commit('setSearchResultSpots', spotList);
    })
      .catch((e) => {
        commit('isSearchingSpot', false);
        commit('setErrorMessageIdSpot', 'common_connection_error');
        console.log(e, payload);
      });
  },

  /**
   * 指定した記事IDから、
   * 記事で紹介されているスポット情報を取得する。
   */
  searchSpotListByArticle: ({commit}, payload) => {
    commit('isSearchingSpot', true);
    actions.resetSearchParams({commit});

    new Merengue(payload.siteId).getSpotsFromArticle(payload.siteId, payload.lang, payload.articleId)
      .then((res) => {
        commit('isSearchingSpot', false);
        commit('setErrorMessageIdSpot', '');

        let spotList = res.data.spotList;
        if (!spotList || spotList.length === 0) {
          // 結果なし
          commit('setErrorMessageIdSpot', 'common_search_none');
          spotList = [];
        } else {
          spotList = spotList.map(spot => new Spot(spot));
        }
        commit('setSearchResultSpots', spotList);
      })
      .catch((e) => {
        commit('isSearchingSpot', false);
        commit('setErrorMessageIdSpot', 'common_connection_error');
        console.log(e, payload);
      });
  },

  /**
   * スポット検索画面を初期化します。
   * @param commit
   * @param state
   */
  initSpotList: ({commit, state}) => {
    store.dispatch('map/setSelectedSpotTab', 'spotSearch', {root: true});
    commit('setTapSpot', null);
    commit('isSearchingSpot', false);
    commit('setErrorMessageIdSpot', '');
  },

  /**
   * スポット検索フィルタの設定を一括解除する
   */
  resetAllFilter({commit}) {
    commit('setFilterCurrentLocation', false);
    commit('setFilterShowArea', false);
    commit('setFilterParking', false);
    commit('setFilterWiFi', false);
    commit('setFilterCreditCard', false);
    commit('setFilterPlug', false);
    commit('setFilterMultilingual', false);
    commit('setFilterBarrierFree', false);
    commit('setFilterVegan', false);
    commit('setFilterHighLate', false);
    commit('setFilterArticleSpot', false);
    commit('setFilterNumSeats', 'unset');
  },

  /**
   * スポット検索フィルタ設定保存用：駐車場
   */
  setFilterParking({commit}, payload) {
    commit('setFilterParking', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：Wi-Fi
   */
  setFilterWiFi({commit}, payload) {
    commit('setFilterWiFi', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：クレジットカード
   */
  setFilterCreditCard({commit}, payload) {
    commit('setFilterCreditCard', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：コンセントあり
   */
  setFilterPlug({commit}, payload) {
    commit('setFilterPlug', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：多言語対応
   */
  setFilterMultilingual({commit}, payload) {
    commit('setFilterMultilingual', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：バリアフリー
   */
  setFilterBarrierFree({commit}, payload) {
    commit('setFilterBarrierFree', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：ヴィーガン
   */
  setFilterVegan({commit}, payload) {
    commit('setFilterVegan', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：高評価
   */
  setFilterHighLate({commit}, payload) {
    commit('setFilterHighLate', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：記事スポット
   */
  setFilterArticleSpot({commit}, payload) {
    commit('setFilterArticleSpot', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：地図で表示しているエリア
   */
  setFilterShowArea({commit}, payload) {
    commit('setFilterShowArea', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：座席数
   */
  setFilterNumSeats({commit}, payload) {
    commit('setFilterNumSeats', payload);
  },

  /**
   * スポット検索フィルタ設定保存用：現在地周辺
   */
  setFilterCurrentLocation({commit}, payload) {
    commit('setFilterCurrentLocation', payload);
  },

  /**
   * ユーザーの現在位置を保存
   */
  setCurrentLocation({commit}, payload) {
    commit('setCurrentLocation', payload);
  },

  setPoiSearchLog({commit}, payload) {
    // POI検索改善ログ処理
    commit('setPoiSearchLog', new PoiSearchLog(payload.merengue, payload.siteId, payload.lang));
  },
  sendPoiSearchLogToSendSelectTiming({state}, payload) {
    if (state.poiSearchLog && state.searchParams.word) {
      if (payload.poi.type === 'address') {
        state.poiSearchLog.sendSelectAddressTiming(state.searchParams.word, state.searchResultAddresses, '', payload.moveType, payload.poi);
      } else if (payload.poi.type === 'spot') {
        state.poiSearchLog.sendSelectSpotTiming(state.searchParams.word, state.searchResultSpots, '', payload.moveType, payload.poi);
      }
    }
  },
  sendPoiSearchLogToUnselectAutocompleteTiming({state}, payload) {
    if (state.poiSearchLog) {
      state.poiSearchLog.sendUnselectAutocompleteTiming(payload.word, payload.list, '', payload.moveType);
    }
  },
  sendPoiSearchLogToSelectAutocompleteTiming({state}, payload) {
    if (state.poiSearchLog) {
      state.poiSearchLog.sendSelectAutocompleteTiming(payload.word, payload.list, '', payload.moveType, payload.spot);
    }
  },
};

export {actions as default};
